import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS_INFO } from '../../../mocks/lisaMerkleData';

const CREDENTIALS = [
  { label: 'Education', value: 'UCLA Design Program', icon: 'ri-graduation-cap-line' },
  { label: 'Training', value: 'Top Beverly Hills Celebrity Design Firms', icon: 'ri-building-4-line' },
  { label: 'Design Style', value: 'Classical European · Modern California', icon: 'ri-palette-line' },
  { label: 'Publications', value: 'Elle Décor · Architectural Digest', icon: 'ri-newspaper-line' },
  { label: 'Specialization', value: 'Luxury Residential · High-End Remodels', icon: 'ri-home-4-line' },
  { label: 'Client Roster', value: 'Household Names · Business Leaders · Investors', icon: 'ri-vip-crown-line' },
];

export default function AboutStorySection() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => setTimeout(() => node.classList.add('revealed'), i * 80));
        obs.disconnect();
      }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white py-28">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-0 border border-stone/20 mb-20">
          {/* Content */}
          <div className="reveal p-12 lg:p-16 flex flex-col justify-center">
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Our Story</span>
            <h2 className="font-display text-4xl md:text-5xl font-light text-charcoal mt-4 mb-8 leading-tight">
              Formally Trained.<br />
              <em className="not-italic text-stone-dark">Distinctly Personal.</em>
            </h2>
            <div className="flex flex-col gap-4">
              <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                Lisa Merkle Design was founded on a powerful belief: that truly exceptional interior design is both an art form and a strategic investment. Every space we design should not only be breathtakingly beautiful — it should enhance the way you live and protect the long-term value of your property.
              </p>
              <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                Lisa is honorably trained at <strong className="text-charcoal font-medium">UCLA</strong>, shaped by years at the best celebrity design firms in Los Angeles. Her formal training encompasses classical French, Italian, and Spanish design, antiquities, and construction details — a foundation informing every material choice and spatial decision.
              </p>
              <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                We interpret projects with sophisticated classical knowledge in a fresh modern lens — represented in high quality work, finishes, design details, vendors, and construction selections. Featured in <strong className="text-charcoal font-medium">Elle Décor</strong> and <strong className="text-charcoal font-medium">Architectural Digest</strong>.
              </p>
              <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                Lisa Merkle Design is honored to direct projects for an impressive roster featuring household names, heads of business, families, and numerous property investors.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link to="/location#contact" className="px-7 py-3 bg-charcoal text-cream text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-charcoal/80 transition-all cursor-pointer whitespace-nowrap">
                Begin Your Project
              </Link>
              <a href={BUSINESS_INFO.phoneHref} className="px-7 py-3 border border-charcoal/20 text-charcoal text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-charcoal/5 transition-all cursor-pointer whitespace-nowrap">
                {BUSINESS_INFO.phone}
              </a>
            </div>
          </div>

          {/* Image stack */}
          <div className="reveal reveal-delay-2 relative min-h-[520px] overflow-hidden">
            <img
              src="https://lisamerkledesign.com/wp-content/uploads/2022/12/275780814_270336268610853_2564379650075167050_n.jpg"
              alt="Lisa Merkle Design — luxury interior detail"
              className="w-full h-full object-cover object-top absolute inset-0 transition-transform duration-1000 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
            <div className="absolute top-8 right-8 w-40 h-48 overflow-hidden border-4 border-white">
              <img
                src="https://lisamerkledesign.com/wp-content/uploads/2022/12/248786837_348115770447022_8742611276494316730_n.jpg"
                alt="Lisa Merkle Design — luxury lighting"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Credentials */}
        <div className="reveal border-t border-stone/20 pt-16">
          <div className="text-center mb-12">
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Studio Credentials</span>
            <h3 className="font-display text-3xl md:text-4xl font-light text-charcoal mt-3">
              Built on Experience, Defined by Excellence
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-stone/15">
            {CREDENTIALS.map((cred, i) => (
              <div key={cred.label} className={`reveal bg-white p-8 hover:bg-cream-warm transition-colors duration-300 ${i < 3 ? `reveal-delay-${i + 1}` : ''}`}>
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center border border-stone/25 flex-shrink-0">
                    <i className={`${cred.icon} text-stone text-base`} />
                  </div>
                  <div>
                    <p className="text-charcoal/35 text-[10px] tracking-[0.2em] uppercase font-sans mb-1">{cred.label}</p>
                    <p className="text-charcoal font-sans font-medium text-sm leading-snug">{cred.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
